'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import { Link } from '@/components/Link';
import type { MagazineCategory } from '@/lib/api';
import { useMagazineHref } from '@/lib/magazine/useMagazineHref';
import type { MagazineArticleListFilter } from '@/src/widgets/magazine-article-list/types';

const pillClassName = (active: boolean) =>
  classNames(
    'shrink-0 rounded-full border px-6 py-2 whitespace-nowrap',
    active
      ? 'bg-primary border-primary text-white'
      : 'border-primary text-primary',
  );

/**
 * Renders as part of the Article List block's output, never on its own, so
 * editors cannot move or remove it independently of the grid it filters.
 *
 * Every pill is a link, not a toggle — filtering happens server-side, so the
 * filter keeps working without JavaScript and across load-more batches.
 */
export const CategoryFilterBar = ({
  categories,
  filter,
}: {
  categories: MagazineCategory[];
  filter: MagazineArticleListFilter;
}) => {
  const t = useTranslations('magazine');
  const magazineHref = useMagazineHref();
  const activeSlug = filter.kind === 'category' ? filter.slug : null;
  const listingHref = magazineHref('listing');

  // A brand that serves no category URL (fit4school.ch) gets no pills at all,
  // only the leading `Alle` — the pill would otherwise link nowhere.
  // Pre-sorted by (`order`, `title`) in the resolver: no client sort.
  const pills = categories.flatMap((category) => {
    const href = magazineHref('category', category.slug);
    return href ? [{ ...category, href }] : [];
  });

  return (
    <div className="mb-6">
      <div className="flex gap-2 overflow-x-auto pb-2">
        {listingHref && (
          <Link
            href={listingHref}
            className={pillClassName(activeSlug === null)}
            aria-current={activeSlug === null ? 'page' : undefined}
          >
            {t('allCategories')}
          </Link>
        )}
        {pills.map((category) => (
          <Link
            key={category.slug}
            href={category.href}
            className={pillClassName(activeSlug === category.slug)}
            aria-current={activeSlug === category.slug ? 'page' : undefined}
          >
            {category.title}
          </Link>
        ))}
      </div>
    </div>
  );
};
